<template>
  <v-container
    fluid
    class="pa-0"
  >
    <v-row
      v-if="hasAccessToSettingsModule"
      class="justify-center align-start"
    >
      <v-col>
        <!-- Header -->
        <v-row class="justify-space-between px-2 mb-4">
          <v-col class="col-auto">
            <span class="headline font-weight-bold">Settings</span>
          </v-col>

          <v-col class="col-auto">
            <v-row>
              <v-col>
                <v-btn
                  color="primary"
                  small
                  :loading="isLoading"
                  @click="saveAllSettings"
                >
                  <v-icon
                    left
                    small
                  >
                    save
                  </v-icon>
                  Save Changes
                </v-btn>

                <v-btn
                  color="grey"
                  small
                  outlined
                  class="ml-2"
                  @click="exportSettings"
                >
                  <v-icon
                    left
                    small
                  >
                    download
                  </v-icon>
                  Export
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col>
            <v-divider />
          </v-col>
        </v-row>

        <!-- Error Alert -->
        <v-row v-if="hasError">
          <v-col>
            <v-alert
              type="error"
              dismissible
              @input="clearError"
            >
              {{ errorMessage }}
            </v-alert>
          </v-col>
        </v-row>

        <!-- Settings Need Attention Alert -->
        <v-row v-if="needsAttention">
          <v-col>
            <v-alert
              type="warning"
              border="left"
              colored-border
              icon="warning"
              class="mb-4"
            >
              <div class="font-weight-medium">
                Your settings need attention
              </div>
              <div class="caption">
                Profile completeness: {{ profileCompleteness }}% |
                Security score: {{ securityScore }}%
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <!-- Navigation Sidebar -->
          <v-col
            class="col-auto"
            style="min-width: 300px;"
          >
            <v-card class="elevation-0">
              <v-card-title>
                <v-col class="py-0">
                  <span class="subtitle-2">Settings</span>
                </v-col>
              </v-card-title>

              <v-card-text>
                <!-- User Profile Summary -->
                <v-row class="mb-4">
                  <v-col>
                    <v-avatar
                      size="48"
                      color="primary"
                      class="mr-3"
                    >
                      <span class="white--text text-h6">
                        {{ userInitials }}
                      </span>
                    </v-avatar>
                    <div class="d-inline-block">
                      <div class="font-weight-medium">
                        {{ fullName }}
                      </div>
                      <div class="caption text--secondary">
                        {{ userEmail }}
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="mb-4" />

                <!-- Navigation Menu -->
                <v-list dense>
                  <v-list-item-group
                    v-model="selectedTabIndex"
                    color="primary"
                  >
                    <v-list-item
                      v-for="(tab, index) in settingsTabs"
                      :key="tab.value"
                      :value="index"
                      @click="selectTab(tab.value)"
                    >
                      <v-list-item-icon>
                        <v-icon>{{ tab.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ tab.title }}</v-list-item-title>
                        <v-list-item-subtitle v-if="tab.subtitle">
                          {{ tab.subtitle }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="tab.badge">
                        <v-chip
                          small
                          :color="tab.badgeColor"
                          text-color="white"
                        >
                          {{ tab.badge }}
                        </v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Main Content Area -->
          <v-col>
            <v-card class="elevation-0">
              <v-card-text class="pa-6">
                <!-- Loading State -->
                <div
                  v-if="isLoading"
                  class="text-center py-8"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                  />
                  <div class="mt-4 text--secondary">
                    Loading settings...
                  </div>
                </div>

                <!-- Settings Content -->
                <div v-else>
                  <!-- Profile Tab -->
                  <settings-profile
                    v-show="selectedTab === 'profile'"
                    :profile="profile"
                    :loading="isLoading"
                    @update="updateProfile"
                  />

                  <!-- Preferences Tab -->
                  <settings-preferences
                    v-show="selectedTab === 'preferences'"
                    :preferences="preferences"
                    :loading="isLoading"
                    @update="updatePreferences"
                  />

                  <!-- Notifications Tab -->
                  <settings-notifications
                    v-show="selectedTab === 'notifications'"
                    :notifications="notifications"
                    :loading="isLoading"
                    @update="updateNotifications"
                  />

                  <!-- Security Tab -->
                  <settings-security
                    v-show="selectedTab === 'security'"
                    :security="security"
                    :loading="isLoading"
                    @update="updateSecurity"
                  />

                  <!-- Privacy Tab -->
                  <settings-privacy
                    v-show="selectedTab === 'privacy'"
                    :privacy="privacy"
                    :loading="isLoading"
                    @update="updatePrivacy"
                  />

                  <!-- Permissions Tab -->
                  <settings-permissions
                    v-show="selectedTab === 'permissions'"
                    :loading="isLoading"
                  />

                  <!-- Demo Mode Tab (for internal users) -->
                  <settings-demo
                    v-show="selectedTab === 'demo' && showDemoSettings"
                    :demo-mode="demoMode"
                    :loading="isLoading"
                    @update="updateDemoMode"
                  />

                  <!-- Alias Tab (for users with permissions) -->
                  <settings-alias
                    v-show="selectedTab === 'alias' && showAliasSettings"
                    :alias-settings="aliasSettings"
                    :loading="isLoading"
                    @update="updateAliasSettings"
                  />

                  <!-- Debug Tab (for developers) -->
                  <settings-debug
                    v-show="selectedTab === 'debug' && showDebugSettings"
                    :current-user="currentUser"
                    :loading="isLoading"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- No Access Message -->
    <v-row
      v-else
      class="justify-center align-center"
      style="min-height: 400px;"
    >
      <v-col class="text-center">
        <v-icon
          size="64"
          color="grey lighten-1"
          class="mb-4"
        >
          settings
        </v-icon>
        <h3 class="text-h5 font-weight-regular mb-2">
          Settings Not Available
        </h3>
        <p class="text--secondary">
          You don't have permission to access the settings module.
        </p>
      </v-col>
    </v-row>

    <!-- Import Settings Dialog -->
    <v-dialog
      v-model="showImportDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title>Import Settings</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="importFile"
            accept=".json"
            label="Select settings file"
            prepend-icon="upload"
            show-size
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showImportDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!importFile"
            :loading="isLoading"
            @click="importSettings"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

// Import settings components
import SettingsProfile from '../components/SettingsProfile.vue'
import SettingsPreferences from '../components/SettingsPreferences.vue'
import SettingsNotifications from '../components/SettingsNotifications.vue'
import SettingsSecurity from '../components/SettingsSecurity.vue'
import SettingsPrivacy from '../components/SettingsPrivacy.vue'
import SettingsDemo from '../components/SettingsDemo.vue'
import SettingsAlias from '../components/SettingsAlias.vue'
import SettingsDebug from '../components/SettingsDebug.vue'
import SettingsPermissions from '../components/SettingsPermissions.vue'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapSettingsState, mapGetters: mapSettingsGetters, mapActions: mapSettingsActions } = createNamespacedHelpers('settings')

export default {
  name: 'SettingsApp',
  components: {
    SettingsProfile,
    SettingsPreferences,
    SettingsNotifications,
    SettingsSecurity,
    SettingsPrivacy,
    SettingsDemo,
    SettingsAlias,
    SettingsDebug,
    SettingsPermissions,
  },
  data() {
    return {
      showImportDialog: false,
      importFile: null,
    }
  },
  computed: {
    ...mapAuthGetters([
      'hasAccessToSettingsModule',
      'hasAliasPermission',
      'isInternalUser',
      'hasDemoModePermissions',
      'hasCustomRoleDev',
      'currentUser',
    ]),
    ...mapSettingsState([
      'selectedTab',
    ]),
    ...mapSettingsGetters([
      'isLoading',
      'hasError',
      'errorMessage',
      'userSettings',
      'profile',
      'preferences',
      'notifications',
      'security',
      'privacy',
      'demoMode',
      'aliasSettings',
      'fullName',
      'userEmail',
      'profileCompleteness',
      'securityScore',
      'needsAttention',
    ]),

    userInitials() {
      const { profile } = this
      const first = profile.firstName ? profile.firstName.charAt(0) : ''
      const last = profile.lastName ? profile.lastName.charAt(0) : ''
      return (first + last).toUpperCase() || '?'
    },

    selectedTabIndex: {
      get() {
        return this.settingsTabs.findIndex((tab) => tab.value === this.selectedTab)
      },
      set(index) {
        const tab = this.settingsTabs[index]
        if (tab) {
          this.selectTab(tab.value)
        }
      },
    },

    showDemoSettings() {
      return this.isInternalUser && this.hasDemoModePermissions
    },

    showAliasSettings() {
      return this.hasAliasPermission
    },

    showDebugSettings() {
      return this.hasCustomRoleDev
    },

    settingsTabs() {
      const tabs = [
        {
          title: 'Profile',
          subtitle: 'Personal information',
          value: 'profile',
          icon: 'account_circle',
          badge: this.profileCompleteness < 80 ? `${this.profileCompleteness}%` : null,
          badgeColor: this.profileCompleteness < 80 ? 'orange' : 'green',
        },
        {
          title: 'Permissions',
          subtitle: 'Access & roles',
          value: 'permissions',
          icon: 'admin_panel_settings',
        },
        {
          title: 'Preferences',
          subtitle: 'Theme and display',
          value: 'preferences',
          icon: 'tune',
        },
        {
          title: 'Notifications',
          subtitle: 'Email and alerts',
          value: 'notifications',
          icon: 'notifications',
        },
        {
          title: 'Security',
          subtitle: 'Password and 2FA',
          value: 'security',
          icon: 'security',
          badge: this.securityScore < 60 ? 'Low' : null,
          badgeColor: 'red',
        },
        {
          title: 'Privacy',
          subtitle: 'Data sharing',
          value: 'privacy',
          icon: 'privacy_tip',
        },
      ]

      // Add demo mode tab for internal users
      if (this.showDemoSettings) {
        tabs.push({
          title: 'Demo Mode',
          subtitle: 'Development features',
          value: 'demo',
          icon: 'science',
        })
      }

      // Add alias tab for users with permission
      if (this.showAliasSettings) {
        tabs.push({
          title: 'Alias',
          subtitle: 'User impersonation',
          value: 'alias',
          icon: 'person_search',
        })
      }

      // Add debug tab for developers
      if (this.showDebugSettings) {
        tabs.push({
          title: 'Debug',
          subtitle: 'Developer tools',
          value: 'debug',
          icon: 'bug_report',
          badge: 'DEV',
          badgeColor: 'purple',
        })
      }

      return tabs
    },
  },

  async created() {
    if (this.hasAccessToSettingsModule) {
      try {
        await this.initializeSettings()
      } catch (error) {
        this.$log.error('Failed to initialize settings:', error)
      }
    }
  },

  methods: {
    ...mapSettingsActions([
      'initializeSettings',
      'updateProfile',
      'updatePreferences',
      'updateNotifications',
      'updateSecurity',
      'updatePrivacy',
      'updateDemoMode',
      'updateAliasSettings',
      'exportSettings',
      'importSettings',
      'clearError',
      'showSettingsView',
    ]),

    selectTab(tab) {
      this.showSettingsView(tab)
    },

    async saveAllSettings() {
      try {
        // This would typically save any pending changes
        // For now, settings are saved individually as they change
        this.$store.dispatch('messages/showSuccess', 'Settings saved successfully', { root: true })
      } catch (error) {
        this.$store.dispatch('messages/showError', 'Failed to save settings', { root: true })
      }
    },

    async handleImportSettings() {
      if (!this.importFile) return

      try {
        const fileContent = await this.readFileAsText(this.importFile)
        const importData = JSON.parse(fileContent)

        await this.importSettings(importData)

        this.showImportDialog = false
        this.importFile = null
        this.$store.dispatch('messages/showSuccess', 'Settings imported successfully', { root: true })
      } catch (error) {
        this.$store.dispatch('messages/showError', 'Failed to import settings', { root: true })
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsText(file)
      })
    },

    openImportDialog() {
      this.showImportDialog = true
    },
  },
}
</script>

<style scoped>
.settings-sidebar {
  border-right: 1px solid #e0e0e0;
}

.settings-content {
  min-height: 600px;
}

.user-avatar {
  display: inline-block;
  vertical-align: middle;
}

.nav-item-badge {
  font-size: 0.75rem;
}
</style>
